import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExecutiveSummaryTab({ projectId, token, initialSummary }) {
  const [execSummary, setExecSummary] = useState(initialSummary || null);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const generateExecutiveSummary = useCallback(async () => {
    setGeneratingSummary(true);
    try {
      const response = await fetch(`${API}/ai/generate-executive-summary?project_id=${projectId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to generate summary");
      const data = await response.json();
      setExecSummary(data);
      toast.success("Executive summary generated");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingSummary(false);
    }
  }, [projectId, token]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold">Executive Summary</h3>
          <p className="text-sm text-muted-foreground">Generate a board-ready summary of your project</p>
        </div>
        <Button onClick={generateExecutiveSummary} disabled={generatingSummary} className="gap-2" data-testid="generate-summary-btn">
          {generatingSummary ? (<><Loader2 className="w-4 h-4 animate-spin" />Generating...</>) : (<><FileText className="w-4 h-4" />Generate Summary</>)}
          <CreditCostTag cost={15} />
        </Button>
      </div>

      {execSummary && (
        <div className="space-y-6">
          <div>
            <h2 className="font-heading text-xl font-bold mb-2">{execSummary.title || "Project Summary"}</h2>
            {execSummary.executive_summary && <p className="text-muted-foreground">{execSummary.executive_summary}</p>}
          </div>

          {execSummary.problem_overview && (
            <div className="p-4 bg-background border border-border rounded-lg">
              <h4 className="font-medium mb-2">Problem Overview</h4>
              <p className="text-sm text-muted-foreground">{execSummary.problem_overview}</p>
            </div>
          )}

          {execSummary.key_findings?.length > 0 && (
            <div>
              <h4 className="font-medium mb-3">Key Findings</h4>
              <ul className="space-y-2">
                {execSummary.key_findings.map((finding, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><span className="text-primary">&bull;</span><span>{finding}</span></li>
                ))}
              </ul>
            </div>
          )}

          {execSummary.financial_impact && (
            <div className="p-4 bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/20 rounded-lg">
              <h4 className="font-medium mb-2 text-[hsl(var(--success))]">Financial Impact</h4>
              <p className="text-sm">{execSummary.financial_impact}</p>
            </div>
          )}

          {execSummary.recommendations?.length > 0 && (
            <div>
              <h4 className="font-medium mb-3">Recommendations</h4>
              <ul className="space-y-2">
                {execSummary.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><span className="text-[hsl(var(--warning))]">{i + 1}.</span><span>{rec}</span></li>
                ))}
              </ul>
            </div>
          )}

          {execSummary.next_steps?.length > 0 && (
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <h4 className="font-medium mb-2">Next Steps</h4>
              <ul className="space-y-1">
                {execSummary.next_steps.map((step, i) => <li key={i} className="text-sm">&rarr; {step}</li>)}
              </ul>
            </div>
          )}

          {!execSummary.ai_enabled && (
            <p className="text-xs text-muted-foreground">Note: AI-powered summary not available. Configure OpenAI API key for enhanced summaries.</p>
          )}
        </div>
      )}

      {!execSummary && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Click "Generate Summary" to create a board-ready executive summary</p>
        </div>
      )}
    </div>
  );
}
